import type { CSSProperties } from "react";
import { StatusPill } from "./ExposureCard";
import type { RemovalTicket } from "./TicketCard";

export type ProofDrawerProps = {
  ticket: RemovalTicket | null;
  open: boolean;
  onClose: () => void;
  onToast?: (msg: string) => void;
};

const NAVY = "#0b1f4a";
const LINK = "#1a3a7a";
const ACCENT_700 = "#7d5411";

export default function ProofDrawer({ ticket, open, onClose, onToast }: ProofDrawerProps) {
  if (!open || !ticket) return null;

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(11,31,74,0.28)",
    zIndex: 60,
  };
  const panel: CSSProperties = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 430,
    maxWidth: "100vw",
    background: "#fff",
    borderLeft: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
    boxShadow: "0 12px 32px color-mix(in srgb, #2d2b2b 22%, transparent)",
    zIndex: 61,
    padding: "30px 32px",
    overflowY: "auto",
    boxSizing: "border-box",
  };

  const copyCase = () => {
    try {
      void navigator.clipboard?.writeText(ticket.caseId);
    } catch {
      /* mock */
    }
    onToast?.("Ärende-ID kopierat");
  };

  return (
    <>
      <div style={overlay} onClick={onClose} aria-hidden />
      <aside style={panel} role="dialog" aria-label={`Bevis ${ticket.source}`} data-testid="proof-drawer">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 600,
              fontSize: 24,
              margin: 0,
              color: NAVY,
              flex: 1,
            }}
          >
            Bevis — {ticket.source}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng"
            style={{
              border: 0,
              background: "transparent",
              fontSize: 20,
              color: "#9b9797",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginTop: 8 }}>
          <StatusPill status={ticket.status} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 18,
            border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
            borderRadius: 4,
            padding: "12px 14px",
          }}
        >
          <span style={{ fontSize: 12, color: "#9b9797" }}>Ärende-ID</span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: NAVY,
              fontFeatureSettings: '"tnum" 1',
              flex: 1,
            }}
          >
            {ticket.caseId}
          </span>
          <button
            type="button"
            onClick={copyCase}
            style={{
              border: 0,
              background: "transparent",
              fontSize: 12.5,
              color: LINK,
              cursor: "pointer",
            }}
          >
            Kopiera
          </button>
        </div>

        {ticket.status === "DENIED" && (
          <div
            style={{
              marginTop: 18,
              border: "1px solid #B42318",
              borderRadius: 4,
              padding: "16px 18px",
            }}
          >
            <div
              style={{
                fontSize: 12,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#B42318",
              }}
            >
              Varför det nekades
            </div>
            <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "#201f1d", margin: "8px 0 0" }}>
              {ticket.reason ?? "Ingen motivering tillgänglig."}
            </p>
            <div
              style={{
                fontSize: 12,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#9b9797",
                marginTop: 14,
              }}
            >
              Vad du kan göra
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {(ticket.steps ?? []).map((s) => (
                <div key={s} style={{ fontSize: 13.5, color: "#201f1d", display: "flex", gap: 8 }}>
                  <span style={{ color: ACCENT_700 }}>→</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              style={{
                marginTop: 14,
                padding: "9px 16px",
                border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                borderRadius: 4,
                background: "transparent",
                fontSize: 13,
                color: "#605d5d",
                cursor: "pointer",
              }}
            >
              Kontakta support
            </button>
          </div>
        )}

        {ticket.status === "NEEDS_USER_ACTION" && (
          <div
            style={{
              marginTop: 18,
              border: "1px solid #C47F00",
              borderRadius: 4,
              padding: "16px 18px",
            }}
          >
            <div
              style={{
                fontSize: 12,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#C47F00",
              }}
            >
              Kräver din åtgärd
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {(ticket.steps ?? []).map((s) => (
                <label
                  key={s}
                  style={{
                    fontSize: 13.5,
                    color: "#201f1d",
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" style={{ accentColor: NAVY }} />
                  {s}
                </label>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#9b9797",
            marginTop: 24,
          }}
        >
          Tidslinje
        </div>
        <div
          style={{
            borderLeft: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
            margin: "14px 0 0 5px",
            paddingLeft: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {ticket.timeline.map((ev) => (
            <div key={`${ev.date}-${ev.text}`} style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: -25,
                  top: 4,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  background: ev.done ? "#0F7B4B" : "#b68235",
                  border: "2px solid #fff",
                }}
              />
              <div style={{ fontSize: 12, color: "#9b9797", fontFeatureSettings: '"tnum" 1' }}>
                {ev.date}
              </div>
              <div style={{ fontSize: 13.5, color: "#201f1d", marginTop: 2 }}>{ev.text}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#9b9797",
            marginTop: 24,
          }}
        >
          Underlag
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {ticket.docs.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9b9797" }}>Inga bilagor ännu.</div>
          ) : (
            ticket.docs.map((d) => (
              <div
                key={d}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                  borderRadius: 4,
                  padding: "11px 14px",
                  fontSize: 13,
                  color: "#605d5d",
                }}
              >
                <span style={{ color: ACCENT_700 }}>▤</span>
                <span style={{ flex: 1 }}>{d}</span>
                <span style={{ fontSize: 12, color: LINK, cursor: "pointer" }}>Visa</span>
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
}
